import { ReactElement, useMemo } from 'react'
import { array, object, string } from 'yup'
import { Box, Button, Stack, Typography } from '@mui/material'
import { Form2, TextField } from '@ui/forms'
import { QuestionAnswer, QuestionCard } from '../utils'
import QuestionOptionsField from './QuestionOptionsField'

interface Props {
  question: QuestionCard
  /** The answer once given; `null` when the user dismissed the question. */
  answer: QuestionAnswer | null | undefined
  onAnswer: (answer: QuestionAnswer) => void
  onDismiss: () => void
}

interface FormValues {
  selected: string[]
  comment: string
}

const validationSchema = object({
  selected: array(string()).when('comment', {
    is: (comment: string | undefined) => !comment?.trim(),
    then: schema => schema.min(1, 'Bitte mindestens eine Option wählen'),
  }),
  comment: string(),
})

/**
 * A question of the agent with options (`ask_user`): one choice or several, optionally with free
 * text. Answering sends the chosen ids; the API checks them against the options before the agent
 * gets them. Reusable for any decision the agent needs, not tied to one workflow.
 */
const QuestionItem = ({ question, answer, onAnswer, onDismiss }: Props): ReactElement => {
  const defaultValues = useMemo<FormValues>(
    () => ({ selected: question.preselected, comment: '' }),
    [question.preselected],
  )
  const labels = useMemo(
    () => new Map(question.options.map(option => [option.id, option.label])),
    [question.options],
  )

  return (
    <Box
      sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1 }}
      data-cy="ai-chat-question"
    >
      <Typography variant="body2" fontWeight={500}>
        {question.question}
      </Typography>
      {answer !== undefined ? (
        <Typography variant="caption" color="text.secondary" component="p" mt={0.5}>
          {answer === null
            ? 'Nicht beantwortet'
            : [...answer.selected.map(id => labels.get(id) ?? id), answer.comment]
                .filter(Boolean)
                .join(', ')}
        </Typography>
      ) : (
        <Form2<FormValues>
          defaultValues={defaultValues}
          validationSchema={validationSchema}
          onSubmit={values =>
            onAnswer({ selected: values.selected, comment: values.comment.trim() || null })
          }
          data-cy="ai-chat-question-form"
        >
          <Stack spacing={1} mt={0.5}>
            {question.multiple && (
              <Typography variant="caption" color="text.secondary">
                Mehrfachauswahl möglich
              </Typography>
            )}
            <QuestionOptionsField
              name="selected"
              options={question.options}
              multiple={question.multiple}
            />
            {question.allowComment && (
              <TextField name="comment" placeholder="Sonstiges (optional)" multiline rows={2} />
            )}
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button
                size="small"
                color="inherit"
                onClick={onDismiss}
                data-cy="ai-chat-question-dismiss"
              >
                Nicht beantworten
              </Button>
              <Button
                size="small"
                variant="contained"
                type="submit"
                data-cy="ai-chat-question-submit"
              >
                Antworten
              </Button>
            </Stack>
          </Stack>
        </Form2>
      )}
    </Box>
  )
}

export default QuestionItem
